(function () {
  'use strict';
  // Public display only. Shopify remains the source of truth at checkout.
  function validCache(cache, now, currency) {
    return cache?.version === 1 && cache.currency === currency &&
      Number.isFinite(cache.updatedAt) && cache.updatedAt <= now + 60000 &&
      cache.validUntil > now && cache.validUntil - cache.updatedAt <= 1800000 &&
      Array.isArray(cache.rules) && cache.rules.length <= 40;
  }
  function bestRule(cache, product, variant, now, currency) {
    if (!variant?.available || product.variantCount > 50 || product.requiresPlan || !validCache(cache, now, currency)) return null;
    let best = null;
    for (const rule of cache.rules) {
      if (!(rule.percent > 0 && rule.percent <= 100) || now < rule.start || (rule.end && now >= rule.end)) continue;
      const matches = rule.all || rule.products.includes(String(product.id)) ||
        rule.variants.includes(String(variant.id)) || rule.collections.some(id => product.collections.includes(id));
      if (!matches) continue;
      const saving = Math.floor(variant.price * rule.percent / 100 + 1e-8);
      if (!saving) continue;
      if (!best || saving > best.saving || (saving === best.saving && (rule.end || Infinity) > (best.end || Infinity))) {
        best = { ...rule, saving, price: variant.price - saving, original: variant.price };
      }
    }
    return best;
  }
  function remaining(end, now) {
    const seconds = Math.max(0, Math.ceil((end - now) / 1000));
    return { days: Math.floor(seconds / 86400), hours: Math.floor(seconds / 3600) % 24, minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60 };
  }
  if (typeof module !== 'undefined') module.exports = { validCache, bestRule, remaining };
  if (typeof document === 'undefined' || customElements.get('siwl107-promotion-price')) return;

  const elements = new Set();
  let data, serverAt, monotonicAt, pending = false, lastFetch = 0;
  function adopt(next) {
    if (!next || !Number.isFinite(next.serverNow)) return false;
    const elapsedServerTime = Number.isFinite(serverAt) ? serverAt + performance.now() - monotonicAt : 0;
    data = next;
    // A cached response must never restart or move the countdown backwards.
    serverAt = Math.max(next.serverNow, elapsedServerTime);
    monotonicAt = performance.now();
    return true;
  }
  try { adopt(JSON.parse(document.querySelector('[data-siwl107-discount-data]')?.textContent || 'null')); } catch (_) { /* Original prices remain visible. */ }
  const now = () => serverAt + performance.now() - monotonicAt;
  const tick = () => { if (!document.hidden) elements.forEach(element => element.update()); };
  async function refresh() {
    if (pending || document.hidden || !elements.size || Date.now() - lastFetch < 55000) return;
    pending = true;
    lastFetch = Date.now();
    try {
      const url = new URL(location.href);
      url.searchParams.set('section_id', 'siwl107-discount-data');
      url.searchParams.set('_siwl_discount_refresh', String(Math.floor(Date.now() / 60000)));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = JSON.parse(html.querySelector('[data-siwl107-discount-data]')?.textContent || 'null');
      if (next && (!data || next.serverNow > data.serverNow)) adopt(next);
      tick();
    } catch (_) { /* Fail closed when the last confirmed snapshot expires. */ }
    finally { pending = false; }
  }
  const money = cents => new Intl.NumberFormat(data.locale || 'en', {
    style: 'currency', currency: data.currency, currencyDisplay: 'code', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(cents / 100);
  class PromotionPrice extends HTMLElement {
    static get observedAttributes() { return ['data-variant-id', 'data-price', 'data-available']; }
    connectedCallback() {
      try { this.product = JSON.parse(this.querySelector('[data-promotion-product]').textContent); } catch (_) { return; }
      this.offer = this.querySelector('[data-promotion-offer]');
      this.fallback = this.querySelector('[data-promotion-fallback]');
      this.timer = this.querySelector('[data-promotion-countdown]');
      elements.add(this);
      this.update();
      if (!data || !validCache(data.cache, now(), data.currency)) refresh();
    }
    disconnectedCallback() { elements.delete(this); }
    attributeChangedCallback() { if (this.isConnected && this.product) this.update(); }
    update() {
      if (!this.product || !this.offer || !this.fallback) return;
      const time = now();
      let variant, rule;
      if (data && data.currency === 'USD') {
        if (this.hasAttribute('data-dynamic')) {
          variant = { id: this.dataset.variantId, price: Number(this.dataset.price), available: this.dataset.available === 'true' };
          rule = bestRule(data.cache, this.product, variant, time, data.currency);
        } else {
          // Choose the actual lowest available variant, not a discount from a different variant.
          for (const candidate of this.product.variants) {
            if (!candidate.available) continue;
            const candidateRule = bestRule(data.cache, this.product, candidate, time, data.currency);
            const candidatePrice = candidateRule ? candidateRule.price : candidate.price;
            if (!variant || candidatePrice < (rule ? rule.price : variant.price)) {
              variant = candidate; rule = candidateRule;
            }
          }
        }
      }
      this.offer.hidden = !rule;
      this.fallback.hidden = !!rule;
      if (rule) {
        const key = `${rule.original}:${rule.price}:${data.locale}`;
        if (this.lastPrice !== key) {
          this.querySelector('[data-promotion-original-value]').textContent = money(rule.original);
          const price = money(rule.price);
          this.querySelector('[data-promotion-sale-value]').textContent = this.product.varies && !this.hasAttribute('data-dynamic') ? data.labels.from.replace('__PRICE__', price) : price;
          this.lastPrice = key;
        }
      }
      if (!this.timer) return;
      this.timer.hidden = !rule?.end;
      if (!rule?.end) return;
      const left = remaining(rule.end, time);
      const dayText = left.days ? data.labels.days.replace('__COUNT__', String(left.days)) + ' ' : '';
      const clock = [left.hours, left.minutes, left.seconds].map(n => String(n).padStart(2, '0')).join(':');
      const text = `${data.labels.remaining} ${dayText}${clock} (${data.labels.saving.replace('__AMOUNT__', money(rule.saving))})`;
      if (this.timer.textContent !== text) this.timer.textContent = text;
    }
  }
  customElements.define('siwl107-promotion-price', PromotionPrice);
  setInterval(tick, 1000);
  setInterval(refresh, 60000);
  document.addEventListener('visibilitychange', () => { tick(); refresh(); });
  window.addEventListener('pageshow', () => { tick(); refresh(); });
  window.addEventListener('focus', () => { tick(); refresh(); });
})();
